/**
 * ImportQueueStrip — "Scorecard imports" section on Home (#1169).
 *
 * Shows the user's active OCR import jobs (GET /api/ocr/scorecard?active=true):
 *   Reading…         — pending/queued/running: spinner + relative submit time
 *   Ready to review  — done with no linked round: CTA to /imports/:jobId
 *   Failed           — friendly error, Dismiss, and a "Try again" link
 *
 * Renders nothing when the queue is empty. Polls every ~10s ONLY while at
 * least one job is still in flight; polling stops once every job is terminal.
 * When a job transitions to done while the strip is mounted, a toast appears
 * ("Scorecard ready to review").
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApiData } from "../lib/hooks";
import { ErrorMessage } from "./ErrorMessage";
import { parseUTC } from "../lib/dates";

const IN_FLIGHT = new Set(["pending", "queued", "running"]);
const POLL_MS = 10_000;
const TOAST_MS = 4_000;

/** "submitted just now" / "submitted 5 min ago" / "submitted 3 h ago" */
export function submittedAgo(createdAt, now = new Date()) {
  const d = parseUTC(createdAt);
  if (!d) return "";
  const mins = Math.max(0, Math.floor((now - d) / 60_000));
  if (mins < 1) return "submitted just now";
  if (mins < 60) return `submitted ${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `submitted ${hours} h ago`;
  return `submitted ${Math.floor(hours / 24)} d ago`;
}

/** Map a raw worker error_message to user-facing copy. */
export function friendlyJobError(message) {
  if (message && message.includes("image_unreadable")) {
    return "Couldn't read the photo — try retaking it";
  }
  return "Something went wrong reading this scorecard";
}

function isReady(job) {
  return job.status === "done" && job.round_id == null;
}

function ImportCard({ job, onDismiss, dismissing }) {
  const name = job.draft_summary?.event_name || "Scorecard import";

  if (IN_FLIGHT.has(job.status)) {
    return (
      <div className="iq-card" data-testid={`import-card-${job.job_id}`}>
        <span className="iq-spinner" aria-hidden="true" />
        <div className="iq-card-body">
          <div className="iq-card-name">{name}</div>
          <div className="iq-card-meta">{submittedAgo(job.created_at)}</div>
        </div>
        <span className="iq-chip">Reading…</span>
      </div>
    );
  }

  if (isReady(job)) {
    return (
      <div className="iq-card" data-testid={`import-card-${job.job_id}`}>
        <div className="iq-card-body">
          <div className="iq-card-name">{name}</div>
          <div className="iq-card-meta">{submittedAgo(job.created_at)}</div>
        </div>
        <span className="iq-chip iq-chip--ready">Ready to review</span>
        <Link to={`/imports/${job.job_id}`} className="btn iq-cta">
          Review &amp; approve
        </Link>
      </div>
    );
  }

  // Failed
  return (
    <div className="iq-card iq-card--failed" data-testid={`import-card-${job.job_id}`}>
      <div className="iq-card-body">
        <div className="iq-card-name">{name}</div>
        <div className="iq-card-error">{friendlyJobError(job.error_message)}</div>
      </div>
      <span className="iq-chip iq-chip--failed">Failed</span>
      <Link to="/new-round" className="btn ghost iq-retry">Try again</Link>
      <button
        type="button"
        className="btn ghost iq-dismiss"
        onClick={() => onDismiss(job.job_id)}
        disabled={dismissing === job.job_id}
      >
        Dismiss
      </button>
    </div>
  );
}

export function ImportQueueStrip() {
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [actionError, setActionError] = useState(null);
  const prevStatuses = useRef(null);

  const { data, error } = useApiData(() => api.listOcrJobs({ active: true }), [tick]);
  const items = data?.items ?? [];

  // Toast when a job we previously saw in flight transitions to done.
  useEffect(() => {
    if (!data) return;
    const prev = prevStatuses.current;
    if (prev) {
      const becameReady = data.items.some(
        (j) => isReady(j) && IN_FLIGHT.has(prev.get(j.job_id))
      );
      if (becameReady) setToast("Scorecard ready to review");
    }
    prevStatuses.current = new Map(data.items.map((j) => [j.job_id, j.status]));
  }, [data]);

  // Poll only while at least one job is non-terminal. Each completed fetch
  // schedules the next one; once every job is terminal nothing re-arms.
  useEffect(() => {
    if (!data || !data.items.some((j) => IN_FLIGHT.has(j.status))) return;
    const id = setTimeout(() => setTick((t) => t + 1), POLL_MS);
    return () => clearTimeout(id);
  }, [data]);

  // Auto-hide the toast.
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(id);
  }, [toast]);

  async function handleDismiss(jobId) {
    setDismissing(jobId);
    setActionError(null);
    try {
      await api.updateOcrJob(jobId, { dismissed: true });
      setTick((t) => t + 1);
    } catch (err) {
      setActionError(err.message || "Could not dismiss the import.");
    } finally {
      setDismissing(null);
    }
  }

  const showSection = items.length > 0 || error || actionError;

  return (
    <>
      {showSection && (
        <section className="panel import-queue" aria-label="Scorecard imports">
          <div className="panel-head">
            <div className="panel-title">Scorecard imports</div>
          </div>
          <ErrorMessage message={error || actionError} />
          <div className="iq-list">
            {items.map((job) => (
              <ImportCard
                key={job.job_id}
                job={job}
                onDismiss={handleDismiss}
                dismissing={dismissing}
              />
            ))}
          </div>
        </section>
      )}
      {toast && (
        <div className="iq-toast" role="status">
          {toast}
        </div>
      )}
    </>
  );
}
